import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useParams } from "react-router-dom";
import { Skeleton, Stack } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
	getFullInvitationsState,
	resetInvitation,
	setOpenCheckoutDrawer,
	setOpenPricingModal
} from "../../../store/slices/invitations/invitations.slice";
import PricingModal from "../../../Components/Modals/PricingModal";
import { Price } from "../../../store/services/invitations/dto/invitations.dto";
import { getDiscountAmount, getInvitationPrice } from "../../../utils/getInvitationPrice";
import CheckoutDrawer, { CheckoutRowItem } from "../../../Components/Modals/CheckoutDrawer/CheckoutDrawer";
import { formatCurrency } from "../../../utils/formatCurrency";
import { round } from "lodash";
import {
	useLazyGetInvitationLastPaymentQuery,
	useLazyGetManageWeddingInvitationQuery
} from "../../../store/services/invitations/invitations.service";
import { useGetExchangeRateTodayQuery } from "../../../store/services/payment/payment.service";
import { BlackCrown } from "../styles";
import { getFullState as getFullAuthState } from "../../../store/slices/auth/auth.slice";

const ManageWeddingInvitations = () => {
	const params = useParams();
	const id = params.id;
	const [checkoutPlan, setCheckoutPlan] = useState<Price | null>(null);
	const dispatch = useAppDispatch();
	const { user } = useAppSelector(getFullAuthState);
	const [fetchInvitation, { isLoading }] = useLazyGetManageWeddingInvitationQuery();
	const [fetchLastPayment] = useLazyGetInvitationLastPaymentQuery();
	const { data: exchangeRateData } = useGetExchangeRateTodayQuery();
	const { managedInvitation, openPricingModal, openCheckoutDrawer, invitationPayedData } =
		useAppSelector(getFullInvitationsState);

	useEffect(() => {
		if (!id) return;
		fetchInvitation({ id: id || "" });
		fetchLastPayment({ id: id || "" });
	}, [id]);

	useEffect(() => {
		return () => {
			dispatch(resetInvitation());
		};
	}, []);

	const finalPrice = useMemo(
		() =>
			checkoutPlan
				? getInvitationPrice(
						checkoutPlan.amount - invitationPayedData.amount,
						checkoutPlan.is_discount_percentage,
						checkoutPlan.discount
					)
				: 0,
		[checkoutPlan, invitationPayedData]
	);

	const checkoutRows: CheckoutRowItem[] = useMemo(
		() =>
			checkoutPlan
				? [
						{
							icon: () => <BlackCrown />,
							label: checkoutPlan.name,
							sublabel: "One-time payment",
							originalAmount:
								checkoutPlan.discount > 0
									? formatCurrency(round(checkoutPlan.amount, 2), checkoutPlan.currency)
									: undefined,
							amount: formatCurrency(finalPrice, checkoutPlan.currency)
						}
					]
				: [],
		[finalPrice, checkoutPlan]
	);

	return (
		<>
			{isLoading && (
				<Stack spacing={1} width={"430px"} style={{ margin: "0 auto" }}>
					<Skeleton variant={"rectangular"} height={"40vh"} />
					<Skeleton variant={"rectangular"} height={"20vh"} />
					<Skeleton variant={"rectangular"} height={"30vh"} />
				</Stack>
			)}

			{openPricingModal && !!managedInvitation && (
				<PricingModal
					open={true}
					onClose={() => dispatch(setOpenPricingModal(false))}
					onCheckout={plan => {
						setCheckoutPlan(plan);
						dispatch(setOpenCheckoutDrawer(true));
					}}
					data={managedInvitation}
					// exchangeRate={exchangeRateData?.exchangeRate}
				/>
			)}
			{openCheckoutDrawer && checkoutPlan && (
				<CheckoutDrawer
					open={true}
					onClose={() => {
						setCheckoutPlan(null);
						dispatch(setOpenCheckoutDrawer(false));
					}}
					exchangeRate={exchangeRateData?.exchangeRate}
					rows={checkoutRows}
					totalLabel={formatCurrency(finalPrice, checkoutPlan.currency)}
					intentData={{
						original_amount: checkoutPlan.amount,
						amount: finalPrice,
						discount: checkoutPlan.discount,
						is_discount_percentage: checkoutPlan.is_discount_percentage,
						currency: checkoutPlan.currency,
						planId: checkoutPlan.type,
						oldPlanId: managedInvitation?.selectedPricingPlan || 0,
						invitationId: id || "",
						items: [
							{
								name: checkoutPlan.name,
								amount: finalPrice,
								description: checkoutPlan.name,
								quantity: 1
							}
						],
						email: user?.email
					}}
					discountLabel={
						checkoutPlan.discount > 0
							? getDiscountAmount(checkoutPlan.discount, checkoutPlan.currency, checkoutPlan.is_discount_percentage)
							: undefined
					}
				/>
			)}
			<Outlet />
		</>
	);
};

export default ManageWeddingInvitations;
